import React, { useRef, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import './ModalFiltro.css';
import '../../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface ModalFiltroProps {
  isOpen: boolean;
  onClose: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  onPreviewChange?: (startYear: number, startMonth: number, endYear: number, endMonth: number) => void;
  onApplyFilters: (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    searchConsultor?: string
  ) => void;
  initialStartYear?: number;
  initialStartMonth?: number;
  initialEndYear?: number;
  initialEndMonth?: number;
  showSearchConsultor?: boolean;
  initialSearchConsultor?: string;
}

const ModalFiltro: React.FC<ModalFiltroProps> = ({
  isOpen,
  onClose,
  sidebarRef,
  onApplyFilters,
  onPreviewChange,
  initialStartYear,
  initialStartMonth,
  initialEndYear,
  initialEndMonth,
  showSearchConsultor = false,
  initialSearchConsultor = '',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const defaultDate = new Date();
  defaultDate.setDate(1);
  defaultDate.setMonth(defaultDate.getMonth() - 2);
  const defaultYear = defaultDate.getFullYear();
  const defaultMonth = defaultDate.getMonth() + 1;

  const [startYear, setStartYear] = useState<number>(initialStartYear ?? defaultYear);
  const [startMonth, setStartMonth] = useState<number>(initialStartMonth ?? defaultMonth);
  const [endYear, setEndYear] = useState<number>(initialEndYear ?? defaultYear);
  const [endMonth, setEndMonth] = useState<number>(initialEndMonth ?? defaultMonth);

  const [searchConsultor, setSearchConsultor] = useState<string>(initialSearchConsultor ?? '');

  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() - 2);
  const maxYear = maxDate.getFullYear();
  const maxMonth = maxDate.getMonth() + 1; // 1-indexed

  const yearOptions: number[] = [];
  for (let y = 2025; y <= maxYear; y++) {
    yearOptions.push(y);
  }

  const ALLOWED_MONTHS = [
    { num: 1, name: 'Janeiro' },
    { num: 2, name: 'Fevereiro' },
    { num: 3, name: 'Março' },
    { num: 4, name: 'Abril' },
    { num: 5, name: 'Maio' },
    { num: 6, name: 'Junho' },
    { num: 7, name: 'Julho' },
    { num: 8, name: 'Agosto' },
    { num: 9, name: 'Setembro' },
    { num: 10, name: 'Outubro' },
    { num: 11, name: 'Novembro' },
    { num: 12, name: 'Dezembro' },
  ];

  useEffect(() => {
    if (isOpen) {
      setStartYear(initialStartYear ?? defaultYear);
      setStartMonth(initialStartMonth ?? defaultMonth);
      setEndYear(initialEndYear ?? defaultYear);
      setEndMonth(initialEndMonth ?? defaultMonth);
      setSearchConsultor(initialSearchConsultor ?? '');
    }
  }, [isOpen, initialStartYear, initialStartMonth, initialEndYear, initialEndMonth, initialSearchConsultor, defaultYear, defaultMonth]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleApply = () => {
    const startDate = new Date(startYear, startMonth - 1, 1);
    const endDate = new Date(endYear, endMonth - 1, 1);

    if (startDate > endDate) {
      toast.error('A data final não pode ser anterior à data inicial.');
      return;
    }

    setTimeout(() => onApplyFilters(
      startYear,
      startMonth,
      endYear,
      endMonth,
      showSearchConsultor ? searchConsultor : undefined
    ), 0);
    onClose();
  };

  return (
    <div className="modal-filtro-overlay">
      <div className="modal-filtro-content" ref={modalRef}>
        <div className="filtro-modal-card">
          
          {/* Header */}
          <div className="filtro-modal-header">
            <div>
              <h2 className="filtro-modal-title">Filtros do Painel</h2>
              <p className="filtro-modal-subtitle">Selecione o período para consulta dos dados</p>
            </div>
            <button className="close-button" onClick={onClose}>&times;</button>
          </div>

          {/* Body */}
          <div className="filtro-modal-body">
            
            {/* Seção de Período */}
            <div className="filtro-section">
              <h3 className="filtro-section-title">Período de Análise</h3>
              <div className="filtro-grid-4">
                <div className="filtro-field">
                  <label>Ano Inicial</label>
                  <div className="select-wrapper">
                    <select
                      value={startYear}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setStartYear(v);
                        let newMonth = startMonth;
                        if (v === maxYear && startMonth > maxMonth) {
                          newMonth = maxMonth;
                          setStartMonth(maxMonth);
                        }
                        onPreviewChange?.(v, newMonth, endYear, endMonth);
                      }}
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="filtro-field">
                  <label>Mês Inicial</label>
                  <div className="select-wrapper">
                    <select
                      value={startMonth}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setStartMonth(v);
                        onPreviewChange?.(startYear, v, endYear, endMonth);
                      }}
                    >
                      {ALLOWED_MONTHS.filter((m) => startYear < maxYear || m.num <= maxMonth).map((m) => (
                        <option key={m.num} value={m.num}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="filtro-field">
                  <label>Ano Final</label>
                  <div className="select-wrapper">
                    <select
                      value={endYear}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setEndYear(v);
                        let newMonth = endMonth;
                        if (v === maxYear && endMonth > maxMonth) {
                          newMonth = maxMonth;
                          setEndMonth(maxMonth);
                        }
                        onPreviewChange?.(startYear, startMonth, v, newMonth);
                      }}
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="filtro-field">
                  <label>Mês Final</label>
                  <div className="select-wrapper">
                    <select
                      value={endMonth}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setEndMonth(v);
                        onPreviewChange?.(startYear, startMonth, endYear, v);
                      }}
                    >
                      {ALLOWED_MONTHS.filter((m) => endYear < maxYear || m.num <= maxMonth).map((m) => (
                        <option key={m.num} value={m.num}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {showSearchConsultor && (
              <div className="filtro-section" style={{ marginTop: '8px' }}>
                <h3 className="filtro-section-title">Buscar Consultor</h3>
                <div className="filtro-field">
                  <label>Nome do Consultor</label>
                  <input
                    type="text"
                    className="premium-text-input"
                    placeholder="Digite o nome do consultor"
                    value={searchConsultor}
                    onChange={(e) => setSearchConsultor(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleApply();
                      }
                    }}
                  />
                </div>
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="filtro-modal-footer">
            <button type="button" className="filtro-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="filtro-btn-primary" onClick={handleApply}>
              Aplicar Filtros
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ModalFiltro;
